import { useEffect } from 'react';
import { ClinicTheme, RawThemeInput } from '@/types/clinic';

export const useClinicTheme = (
  theme: ClinicTheme | RawThemeInput | null | undefined
) => {
  useEffect(() => {
    if (!theme) return;
    const root = document.documentElement;

    // Normalize both old (camelCase) and new (kebab-case) inputs
    const primary =
      (theme as RawThemeInput).primary ?? (theme as ClinicTheme).primaryColor;
    const secondary =
      (theme as RawThemeInput).secondary ??
      (theme as ClinicTheme).secondaryColor;
    const main =
      (theme as RawThemeInput).main ??
      (theme as ClinicTheme).mainColor ??
      primary;
    const textColor =
      (theme as RawThemeInput)['text-color'] ??
      (theme as ClinicTheme).textColor ??
      '#1f2937';
    const secondTextColor =
      (theme as RawThemeInput)['second-text-color'] ??
      (theme as ClinicTheme).secondTextColor ??
      '#F6DBAE';
    const iconStroke =
      (theme as RawThemeInput)['icon-stroke'] ??
      (theme as ClinicTheme).iconStroke ??
      secondary;
    let gradient =
      (theme as RawThemeInput).gradient ??
      (theme as ClinicTheme).gradient ??
      null;
    // Treat string 'null' as null
    if (gradient === 'null') {
      gradient = null;
    }

    // Set CSS custom properties for the theme
    root.style.setProperty('--clinic-primary', primary);
    root.style.setProperty('--clinic-secondary', secondary);
    root.style.setProperty('--clinic-accent', main || secondary);
    root.style.setProperty('--clinic-text', textColor);
    root.style.setProperty('--clinic-text-secondary', secondTextColor);
    root.style.setProperty('--clinic-main', main || primary);

    // Set icon stroke if provided
    if (iconStroke) {
      root.style.setProperty('--clinic-icon-stroke', iconStroke);
    }

    // Set background based on gradient existence
    if (typeof gradient === 'string' && gradient.trim() !== '') {
      root.style.setProperty('--clinic-gradient', gradient);
      root.style.setProperty('--clinic-background', gradient); // Use gradient as background

      // Add a class to the body to indicate gradient is being used
      document.body.classList.add('use-gradient-background');
    } else {
      // No gradient provided: ensure all gradient usages fall back to the primary color
      root.style.setProperty('--clinic-gradient', primary);
      root.style.setProperty('--clinic-background', primary); // Use primary color as background
      document.body.classList.remove('use-gradient-background');
    }

    // Avoid forcing global text color; allow component-level overrides via CSS variables

    // Apply custom CSS if provided
    const customCSS = (theme as ClinicTheme).customCSS;
    if (customCSS) {
      const styleId = 'clinic-custom-theme';
      const existingStyle = document.getElementById(styleId);

      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = customCSS;
      document.head.appendChild(style);
    }

    // Cleanup: only remove injected custom CSS. Keep CSS variables to persist theme.
    return () => {
      const styleId = 'clinic-custom-theme';
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [theme]);
};
